/**
 * useMediaUsage Hook
 * 
 * Fetches and manages user's media (video/voice) usage limits and current consumption.
 * Used to display countdown timers, warnings, and limit-reached states in the UI.
 */

import { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from "../api/client";
import { AuthContext } from '../App';


// Polling interval for refreshing usage data during active sessions
const USAGE_POLL_INTERVAL_MS = 30000; // 30 seconds

/**
 * @returns {Object} Media usage state and utilities
 *   - loading: boolean
 *   - error: string | null
 *   - usage: {
 *       video_session_limit_minutes: number,
 *       video_daily_limit_minutes: number,
 *       video_minutes_used_today: number,
 *       video_minutes_remaining_today: number,
 *       voice_session_limit_minutes: number,
 *       voice_daily_limit_minutes: number,
 *       voice_minutes_used_today: number,
 *       voice_minutes_remaining_today: number,
 *       media_tier: string,
 *       date: string,
 *       is_admin: boolean,
 *     } | null
 *   - refetch: () => Promise<void>
 *   - canStartVideoCall: boolean
 *   - canStartVoiceCall: boolean
 *   - videoSessionLimitSeconds: number
 *   - voiceSessionLimitSeconds: number
 *   - videoDailyRemainingSeconds: number
 *   - voiceDailyRemainingSeconds: number
 */
export function useMediaUsage({ pollWhileActive = false } = {}) {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);

  const fetchUsage = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await apiClient.get(`/media/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsage(res.data);
      setError(null);
    } catch (err) {
      console.error('[useMediaUsage] Failed to fetch usage:', err);
      setError(err?.response?.data?.detail || 'Failed to load media usage');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial fetch
  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Optional polling for active sessions
  useEffect(() => {
    if (!pollWhileActive || !token) return;

    const interval = setInterval(fetchUsage, USAGE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [pollWhileActive, token, fetchUsage]);

  // Derived values for easier consumption
  const videoSessionLimitSeconds = (usage?.video_session_limit_minutes || 0) * 60;
  const voiceSessionLimitSeconds = (usage?.voice_session_limit_minutes || 0) * 60;
  const videoDailyRemainingSeconds = (usage?.video_minutes_remaining_today || 0) * 60;
  const voiceDailyRemainingSeconds = (usage?.voice_minutes_remaining_today || 0) * 60;

  // Check if user can start a call (has remaining daily minutes)
  const canStartVideoCall = videoDailyRemainingSeconds > 0;
  const canStartVoiceCall = voiceDailyRemainingSeconds > 0;

  // Effective session limit (minimum of session limit and daily remaining)
  const effectiveVideoSessionSeconds = Math.min(
    videoSessionLimitSeconds,
    videoDailyRemainingSeconds
  );
  const effectiveVoiceSessionSeconds = Math.min(
    voiceSessionLimitSeconds,
    voiceDailyRemainingSeconds
  );

  return {
    loading,
    error,
    usage,
    refetch: fetchUsage,
    // Convenience booleans
    canStartVideoCall,
    canStartVoiceCall,
    // Raw limits in seconds
    videoSessionLimitSeconds,
    voiceSessionLimitSeconds,
    videoDailyRemainingSeconds,
    voiceDailyRemainingSeconds,
    // Effective limits (capped by daily remaining)
    effectiveVideoSessionSeconds,
    effectiveVoiceSessionSeconds,
    // Tier info
    mediaTier: usage?.media_tier || 'free',
    isAdmin: usage?.is_admin || false,
  };
}

export default useMediaUsage;
